import { useTranslation } from 'react-i18next';
import {
  ExperienceRemovedNotice,
  TripExperienceFrame,
  useDays,
} from '@koob/trip-designer';
import ComposeMapDay from './ComposeMapDay';
import ComposeMapFrame from './ComposeMapFrame';

// One row of the timeline: an experience frame, a removal being offered back,
// or a day that carries no experience.
export default function ComposeMapBlock({
  block,
  registry,
  onUndo,
  onExperienceRemoved,
  addExperience,
  addHotel,
}) {
  const { t } = useTranslation('tripDesigner');
  const days = useDays();
  const { frame, removal, dayIndices } = block;
  const firstDayIndex = dayIndices[0];
  const lastDayIndex = dayIndices[dayIndices.length - 1];
  const isSingleDayBlock = firstDayIndex === lastDayIndex;

  // §3 — "Day 7" on a single day, "Day 1–6" on a longer
  // block. The frame header and the undo offer name the
  // days the same way.
  const dayRangeLabel = isSingleDayBlock
    ? t('compose.frame.singleDay', { day: firstDayIndex + 1 })
    : t('compose.frame.dayRange', {
        start: firstDayIndex + 1,
        end: lastDayIndex + 1,
      });

  const dayCards = dayIndices.map(index => (
    <ComposeMapDay
      key={`compose-day-${days?.[index]?.id ?? index}`}
      dayIndex={index}
      frameProps={
        frame
          ? {
              globalServicesSlot:
                registry.globalServicesSlots[frame.startDayIndex] ?? null,
              registerRemoveHandler: registry.registerRemoveHandler,
              onExperienceRemoved,
              registerExperienceWarning: registry.registerExperienceWarning,
              insertBeforeHandledByFrame: index === firstDayIndex,
            }
          : undefined
      }
      addExperience={addExperience}
      addHotel={addHotel}
      registerHotelWarning={registry.registerHotelWarning}
    />
  ));

  if (frame) {
    return (
      <ComposeMapFrame
        frame={frame}
        dayCards={dayCards}
        dayRangeLabel={dayRangeLabel}
        registry={registry}
      />
    );
  }

  if (!removal) {
    return <div>{dayCards}</div>;
  }

  // AC09 — the undo offer stands exactly where the frame
  // stood, on the same card: the experience is gone, so the
  // offer takes over the header that carried its cross and
  // the emptied days stay grouped underneath.
  return (
    <TripExperienceFrame
      notice={
        <ExperienceRemovedNotice
          message={t('compose.undo.experienceRemoved', {
            name: removal.experienceName,
          })}
          description={t(
            isSingleDayBlock
              ? 'compose.undo.hintSingleDay'
              : 'compose.undo.hint',
            { days: dayRangeLabel },
          )}
          actionLabel={t('compose.undo.action')}
          onUndo={onUndo}
        />
      }
    >
      {dayCards}
    </TripExperienceFrame>
  );
}
